import { useCallback, useMemo, useState } from "react";
import { Button, Input, Empty, Spin } from "antd";
import {
  DeleteOutlined,
  PlusOutlined,
  PrinterOutlined,
  ReloadOutlined,
} from "@ant-design/icons";
import { useGlobalContext } from "@/hooks/context";
import { wrapPage } from "@/utils/pages";
import PageHeader from "@/components/shared/PageHeader";
import Indicator from "@/components/shared/Indicator";
import { useIntegrationOrders } from "@/components/integration/order/helper";
import {
  IntegrationOrderSearchParams,
  IntegrationOrderStatus,
  IntegrationOrderType,
  IntegrationOrderVo,
} from "@/types/integration";
import { useDebounce } from "@/hooks/shared/debounce";
import { generateInstaWorksheet, queryOrders } from "@/apis/tms/order";
import { TMSOrderType } from "@/types/tms";
import classNames from "classnames";
import B2BOrderItem from "@/components/tms/order/insta/b2b/B2BOrderItem";
import TMSOrderBarcode from "@/components/tms/order/insta/b2b/TMSOrderBarcode";

interface LinkedTMSOrder {
  orderId: string;
  riiOrderNo: string;
}

type SelectedOrder = IntegrationOrderVo & {
  linkedOrder?: LinkedTMSOrder;
};

export function InstaWorksheetPageFC() {
  const { hintPrompt } = useGlobalContext();
  const [customerNumberLike, setCustomerNumberLike] = useState("");
  const debouncedSearch = useDebounce(customerNumberLike, 500);

  const query = useMemo<IntegrationOrderSearchParams>(
    () => ({
      customerName: "INSTA",
      customerNumberLike: debouncedSearch,
      statusList: [IntegrationOrderStatus.Created],
      type: IntegrationOrderType.Business,
      size: 20,
      dirs: ["desc"],
      sorts: ["created_at"],
    }),
    [debouncedSearch],
  );

  const { data: orders, isFetching, refetch } = useIntegrationOrders(query);

  const [selectedOrders, setSelectedOrders] = useState<SelectedOrder[]>([]);
  const [expandedIds, setExpandedIds] = useState<Set<string>>(new Set());

  const selectedIds = useMemo(
    () => new Set(selectedOrders.map((o) => o.id)),
    [selectedOrders],
  );

  const availableOrders = useMemo(() => {
    if (!orders) return [];
    let items = orders.items.filter((o) => !selectedIds.has(o.id));
    if (debouncedSearch) {
      const lower = debouncedSearch.toLowerCase();
      items = items.filter(
        (o) =>
          o.customerNumber?.toLowerCase().includes(lower) ||
          o.docNum?.toLowerCase().includes(lower) ||
          o.warehouseCode?.toLowerCase().includes(lower),
      );
    }
    return items;
  }, [orders, selectedIds, debouncedSearch]);

  const [adding, setAdding] = useState(false);
  const [generating, setGenerating] = useState(false);

  const addOrder = useCallback(
    (order: IntegrationOrderVo) => {
      setAdding(true);
      queryOrders({
        scanOrderProductCustomerOrderNo: order.cubeDeliveryNo,
        typeList: [TMSOrderType.Scan],
      })
        .then((res) => {
          let linkedOrder: LinkedTMSOrder | undefined;
          if (res.items.length === 0) {
            hintPrompt(
              "warning",
              "No Corresponding Order",
              `No TMS scan order found for ${order.customerNumber}`,
            );
          } else {
            const tms = res.items[0];
            linkedOrder = { orderId: tms.id, riiOrderNo: tms.riiOrderNo };
          }
          setSelectedOrders((prev) => {
            if (prev.some((o) => o.id === order.id)) return prev;
            return [...prev, { ...order, linkedOrder }];
          });
        })
        .finally(() => setAdding(false));
    },
    [hintPrompt],
  );

  const removeOrder = useCallback((orderId: string) => {
    setSelectedOrders((prev) => prev.filter((o) => o.id !== orderId));
  }, []);

  const toggleExpand = useCallback((id: string) => {
    setExpandedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  }, []);

  return (
    <div className="flex h-full flex-col">
      <div className="flex flex-row items-center justify-between pb-2">
        <PageHeader
          title="Create Insta Worksheet"
          className="shrink-0 pt-2 pl-2 pb-1"
        />
        <div className="flex flex-row gap-2">
          <Button
            type="primary"
            variant="outlined"
            color="green"
            onClick={() => {
              setTimeout(() => {
                window.open("/order/scan_process", "_blank");
              }, 500);
            }}
          >
            Start Scan Order
          </Button>
          <Button
            type="primary"
            icon={<PrinterOutlined />}
            disabled={selectedOrders.length === 0}
            loading={generating}
            onClick={() => {
              setGenerating(true);
              generateInstaWorksheet(
                selectedOrders
                  .filter((o) => o.linkedOrder)
                  .map((o) => o.linkedOrder!.orderId),
              )
                .then(({ blob, filename }) => {
                  const url = URL.createObjectURL(blob);
                  const a = document.createElement("a");
                  a.href = url;
                  a.download = filename;
                  a.click();
                  URL.revokeObjectURL(url);
                })
                .catch((err) => {
                  hintPrompt(
                    "error",
                    "PDF Generation Failed",
                    err?.message ?? "Failed to generate worksheet PDF",
                  );
                })
                .finally(() => setGenerating(false));
            }}
          >
            Print Worksheet PDF
          </Button>
        </div>
      </div>
      <div className="flex flex-1 gap-2 min-h-0">
        <div
          className={classNames(
            "flex flex-3 flex-col rounded-md",
            "border border-gray-200 bg-white px-2",
          )}
        >
          <div className="flex items-center justify-between shrink-0 py-1 pr-2">
            <Indicator label="Select 2B Orders to include" />
            <Button
              icon={<ReloadOutlined />}
              loading={isFetching}
              onClick={() => refetch()}
            >
              Refresh
            </Button>
          </div>
          <div className="px-2 pb-2 shrink-0">
            <Input.Search
              placeholder="Search by customer no, doc num, warehouse..."
              allowClear
              value={customerNumberLike}
              onChange={(e) => setCustomerNumberLike(e.target.value)}
            />
          </div>
          <div className="flex-1 overflow-y-auto px-2 pb-2">
            {isFetching ? (
              <div className="flex items-center justify-center h-32">
                <Spin />
              </div>
            ) : availableOrders.length === 0 ? (
              <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} />
            ) : (
              availableOrders.map((order) => (
                <B2BOrderItem
                  key={order.id}
                  order={order}
                  expanded={expandedIds.has(order.id)}
                  onToggleExpand={() => toggleExpand(order.id)}
                  action={
                    <Button
                      size="small"
                      type="primary"
                      icon={<PlusOutlined />}
                      disabled={adding}
                      loading={adding}
                      onClick={() => addOrder(order)}
                    />
                  }
                />
              ))
            )}
          </div>
        </div>
        <div
          className={classNames(
            "flex flex-2 flex-col rounded-md border",
            "border-gray-200 bg-white",
          )}
        >
          <div className="flex items-center justify-between shrink-0 py-1 px-2">
            <Indicator
              label="Selected Orders"
              hint={`${selectedOrders.length}`}
            />
            <Button
              danger
              disabled={selectedOrders.length === 0}
              onClick={() => setSelectedOrders([])}
            >
              Clear All
            </Button>
          </div>
          <div className="flex-1 overflow-y-auto">
            {selectedOrders.length === 0 ? (
              <Empty
                className="pt-40"
                image={Empty.PRESENTED_IMAGE_SIMPLE}
                description="No orders selected"
              />
            ) : (
              selectedOrders.map((order, index) => (
                <div
                  key={order.id}
                  className={classNames(
                    "!rounded-none !border-0 !mb-0",
                    index < selectedOrders.length - 1 &&
                      "!border-b !border-solid !border-gray-200",
                  )}
                >
                  <B2BOrderItem
                    className={"!rounded-none !border-0 !mb-0"}
                    order={order}
                    action={
                      <Button
                        size="small"
                        danger
                        icon={<DeleteOutlined />}
                        onClick={() => removeOrder(order.id)}
                      />
                    }
                  />
                  <TMSOrderBarcode value={order.linkedOrder?.riiOrderNo} />
                </div>
              ))
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

export const InstaWorksheetPage = wrapPage(InstaWorksheetPageFC);
